import React, { useMemo, useState } from 'react';

import { useEntitlements } from '../../contexts/Entitlements';
import { MASection } from './MASection';

import './entitlements-sections.scss';

/**
 * "Your Plan" — the VIEW-ONLY panel on the Primary Admin page showing a store
 * owner what their store is on and what that includes (Alex, 2026-08-13).
 *
 * Read-only by design: plans/add-ons are set by RC master admins (and later
 * by Stripe billing) — there is deliberately nothing to click here beyond an
 * expand for the full feature list. Data comes from the entitlements context
 * (GET /api/entitlements for the active store); the `features` map is
 * mode-blind, so the panel shows the true shape of the plan even while
 * enforcement is off.
 *
 * Rules honoured here:
 *  - Future Release features are NEVER shown (not even greyed) — hidden means
 *    not advertised.
 *  - Prices are display constants until Stripe billing (Focus 7) makes the
 *    backend the source of truth for rates.
 */

// Plan slugs are frozen (free/black/custom are what the API sends); only the
// commercial names and prices changed — CORE/PLUS/PRO, 2026-09-17. Prices
// mirror retail-circle.com and stay display constants until Stripe billing
// (Focus 7) makes the backend the source of truth for rates.
const PLAN_DISPLAY = {
  free: { label: 'CORE', price: '£450 / month' },
  black: { label: 'PLUS', price: '£750 / month' },
  custom: { label: 'PRO', price: '£1,950 / month' },
};

const AI_EMPLOYEE_PRICE = '£345 / month';
// Intelligence and Lead Gen are NOT priced on retail-circle.com — the site only
// publishes £345 for the four AI employees. The £275 that used to sit here was
// never confirmed, and showing a merchant a wrong price is worse than showing
// none, so these two say nothing until someone sets the number.
const ADDON_PRICE = 'price on request';

const ADDON_DISPLAY = [
  {
    key: 'addon:ecommerce_manager',
    label: 'E-Commerce Manager',
    price: AI_EMPLOYEE_PRICE,
    blurb: 'Listing creation and marketplace sync — Chrono24, eBay, WatchFinder, WhatsApp & Google Shopping (included from PLUS)',
  },
  {
    key: 'addon:sales_advisor',
    label: 'Sales Advisor',
    price: AI_EMPLOYEE_PRICE,
    blurb: 'Sale quotes & pricing — trade and market price history, data validation',
  },
  {
    key: 'addon:admin_assistant',
    label: 'Admin Assistant',
    price: AI_EMPLOYEE_PRICE,
    blurb: 'Store operations & logistics',
  },
  {
    key: 'addon:accounting',
    label: 'Bookkeeper',
    price: AI_EMPLOYEE_PRICE,
    blurb: 'Accounting & VAT — Xero invoicing, PnL, Reconciliation, commissions, consignment agreements',
  },
  { key: 'addon:intelligence', label: 'Intelligence', price: ADDON_PRICE, blurb: 'PriceIQ, competitive pricing, buying opportunities, sniper alerts' },
  { key: 'addon:lead_gen', label: 'Lead Gen', price: ADDON_PRICE, blurb: 'WhatsApp leads, sourcing outside your network' },
];

const IBadge = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="6" r="3.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M5.5 8.8L4.5 14L8 12L11.5 14L10.5 8.8" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

const Tick = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function UsageMeter({ label, usage }) {
  if (!usage) return null;

  const { max, used } = usage;
  const unlimited = max === null || max === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.round(((used || 0) / Math.max(max, 1)) * 100));
  const atCap = !unlimited && (used || 0) >= max;

  return (
    <div className="yp-meter">
      <div className="yp-meter__head">
        <span>{label}</span>
        <span className="yp-meter__count">
          {unlimited ? `${used || 0} used · unlimited` : `${used || 0} of ${max} used`}
        </span>
      </div>
      {!unlimited && (
        <div className="yp-meter__bar" role="img" aria-label={`${pct}% of ${label.toLowerCase()} used`}>
          <div
            className={`yp-meter__fill ${atCap ? 'yp-meter__fill--full' : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function YourPlanSection() {
  const { loading, failed, refresh, plan, addons, features, limits, catalog, trial_ends_at: trialEndsAt } = useEntitlements();
  const [showAll, setShowAll] = useState(false);

  // Grouped feature lists: what's included, and what a live upgrade could add.
  // Future Release keys are dropped entirely; the two limits render as meters,
  // not list rows.
  const { included, notIncluded } = useMemo(() => {
    const inc = new Map();
    const not = new Map();

    (catalog || []).forEach((f) => {
      if (f.state === 'future' || f.type === 'limit') return;
      const bucket = features[f.key] !== false ? inc : not;
      if (!bucket.has(f.category)) bucket.set(f.category, []);
      // No upgrade unlocks a coming-soon feature yet — say so.
      bucket.get(f.category).push(
        bucket === not && f.state === 'coming_soon' ? `${f.label} (coming soon)` : f.label,
      );
    });

    return { included: Array.from(inc.entries()), notIncluded: Array.from(not.entries()) };
  }, [catalog, features]);

  // The fetch failed. Say so and offer a retry rather than disappearing:
  // this panel used to render nothing on an error, which is identical to how
  // it renders for a store with no plan — so one hiccup on load removed it
  // for the whole session and it looked random (Alex, 2026-10-26).
  if (failed) {
    return (
      <MASection title="Your Plan" icon={<IBadge />} defaultOpen>
        <p className="yp-failed">
          Could not load your plan just now.
          <button type="button" className="yp-failed__retry" onClick={refresh}>Try again</button>
        </p>
      </MASection>
    );
  }

  // Still loading, or a store with genuinely no commercial plan — a master
  // admin on the platform store, for instance. Nothing useful to show.
  if (loading || !plan) return null;

  const planMeta = PLAN_DISPLAY[plan] || { label: plan, price: '' };
  const trialActive = trialEndsAt && new Date(trialEndsAt) > new Date();

  return (
    <MASection title="Your Plan" icon={<IBadge />} defaultOpen>
      <div className="yp-header">
        <span className={`ent-plan-chip ent-plan-chip--${plan} yp-header__chip`}>{planMeta.label}</span>
        <span className="yp-header__price">{planMeta.price}</span>
        {trialActive && (
          <span className="yp-header__trial">
            Black trial until {new Date(trialEndsAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        )}
      </div>

      <div className="yp-addons">
        {ADDON_DISPLAY.map((addon) => {
          const active = (addons || []).includes(addon.key);
          return (
            <div key={addon.key} className={`yp-addon ${active ? 'yp-addon--active' : ''}`}>
              <span className="yp-addon__mark">{active ? <Tick /> : '—'}</span>
              <span className="yp-addon__name">
                {addon.label} add-on
                <span className="yp-addon__blurb">{addon.blurb}</span>
              </span>
              <span className="yp-addon__price">{active ? `Included · ${addon.price}` : 'Not included'}</span>
            </div>
          );
        })}
      </div>

      <UsageMeter label="Listings this month" usage={limits?.limit_listings} />
      <UsageMeter label="Team seats" usage={limits?.limit_seats} />

      <div className="yp-features">
        <h5>Included in your plan</h5>
        {included.map(([category, labels]) => (
          <div className="yp-features__group" key={category}>
            <span className="yp-features__category">{category}</span>
            <ul>
              {labels.map((label) => (
                <li key={label}><Tick /> {label}</li>
              ))}
            </ul>
          </div>
        ))}

        {notIncluded.length > 0 && (
          <>
            <button type="button" className="yp-features__toggle" onClick={() => setShowAll((s) => !s)}>
              {showAll ? 'Hide' : 'Show'} what an upgrade adds
            </button>
            {showAll && notIncluded.map(([category, labels]) => (
              <div className="yp-features__group yp-features__group--locked" key={category}>
                <span className="yp-features__category">{category}</span>
                <ul>
                  {labels.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}
      </div>

      <p className="yp-footnote">
        To change your plan, add-ons or limits, contact Retail Circle.
      </p>
    </MASection>
  );
}

export default YourPlanSection;
